import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import Card from "../shared/Card";
import moment from "moment";
import { getEarningsByDateRange } from "../../api/referral";
import DateRangeFilter from "../shared/DateRangeFilter";
import { PuffLoader } from "react-spinners";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const CommissionsChart = () => {
  const getFirstDayOfCurrentMonth = () => {
    const today = new Date();
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      1
    ).toLocaleDateString("en-CA");
  };

  const getCurrentDay = () => {
    return new Date().toLocaleDateString("en-CA");
  };

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Comisiones Diarias",
        data: [],
        borderColor: "#009ee3",
        backgroundColor: "rgba(0, 158, 227, 0.2)",
        tension: 0.3,
        fill: true,
        borderWidth: 2,
        pointBackgroundColor: "#fff",
        pointBorderColor: "#009ee3",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        fullDates: [],
      },
    ],
  });
  const [options, setOptions] = useState({});
  const [totalCommissions, setTotalCommissions] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: getFirstDayOfCurrentMonth(),
    endDate: getCurrentDay(),
  });

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  useEffect(() => {
    const fetchEarningsData = async () => {
      setLoading(true);
      try {
        const earningsData = await getEarningsByDateRange(
          dateRange.startDate,
          dateRange.endDate
        );
        processChartData(earningsData);
      } catch (error) {
        console.error("Error al obtener datos de comisiones:", error);
        processChartData([]);
        setLoading(false);
      }
    };

    fetchEarningsData();
  }, [dateRange]);

  const processChartData = (earningsData) => {
    const runningTotal =
      earningsData && earningsData.length > 0
        ? earningsData.reduce((sum, item) => sum + item.earnings, 0)
        : 0;

    setTotalCommissions(runningTotal);

    if (!earningsData || earningsData.length === 0) {
      setChartData({
        labels: [],
        datasets: [
          {
            label: "Comisiones Diarias",
            data: [],
            borderColor: "#009ee3",
            backgroundColor: "rgba(0, 158, 227, 0.2)",
            tension: 0.3,
            fill: true,
            borderWidth: 2,
            pointBackgroundColor: "#fff",
            pointBorderColor: "#009ee3",
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            fullDates: [],
          },
        ],
      });

      setOptions(getChartOptions([]));
      setLoading(false);
      return;
    }

    earningsData.sort((a, b) => new Date(a.date) - new Date(b.date));

    const labels = earningsData.map((item) =>
      moment.utc(item.date).format("DD MMM")
    );
    const values = earningsData.map((item) => item.earnings);

    const fullDates = earningsData.map((item) =>
      moment.utc(item.date).format("DD/MM/YYYY")
    );

    setChartData({
      labels,
      datasets: [
        {
          label: "Comisiones Diarias",
          data: values,
          borderColor: "#009ee3",
          backgroundColor: "rgba(0, 158, 227, 0.2)",
          tension: 0.3,
          fill: true,
          borderWidth: 2,
          pointBackgroundColor: "#fff",
          pointBorderColor: "#009ee3",
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          fullDates: fullDates,
        },
      ],
    });

    setOptions(getChartOptions(earningsData));
    setLoading(false);
  };

  const getChartOptions = (earningsData) => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: "category",
          title: {
            display: true,
            text: "Fecha",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 45,
            autoSkip: true,
            maxTicksLimit:
              earningsData.length > 20 ? 20 : Math.max(earningsData.length, 1),
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Monto ($)",
            font: {
              size: 14,
              weight: "bold",
            },
          },
          ticks: {
            callback: function (value) {
              return "$" + value.toFixed(2);
            },
          },
        },
      },
      plugins: {
        legend: {
          position: "top",
          labels: {
            boxWidth: 15,
            usePointStyle: true,
            pointStyle: "circle",
            font: {
              size: 12,
            },
          },
        },
        tooltip: {
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          titleFont: {
            size: 14,
            weight: "bold",
          },
          bodyFont: {
            size: 13,
          },
          callbacks: {
            title: function (context) {
              const dataIndex = context[0].dataIndex;
              const dataset = chartData.datasets[0];
              return dataset.fullDates && dataset.fullDates.length > dataIndex
                ? dataset.fullDates[dataIndex]
                : "";
            },
            label: function (context) {
              return "Comisiones: $" + context.parsed.y.toFixed(2);
            },
          },
        },
      },
    };
  };

  return (
    <Card title="Comisiones Generadas" className="commissions-chart-card">
      <div className="commissions-chart">
        <div className="commissions-chart__header">
          <div className="commissions-chart__filters">
            <DateRangeFilter
              dateRange={dateRange}
              onDateChange={handleDateRangeChange}
            />
          </div>
          <div className="commissions-chart__total">
            <h3>Total Acumulado:</h3>
            <p className="commissions-total">COP {totalCommissions}</p>
          </div>
        </div>

        <div className="commissions-chart__container">
          {loading ? (
            <div className="loader-container">
              <PuffLoader
                color="#009ee3"
                loading={true}
                size={60}
                margin={2}
                speedMultiplier={4}
              />
            </div>
          ) : (
            <Line data={chartData} options={options} height={300} />
          )}
        </div>
      </div>
    </Card>
  );
};

export default CommissionsChart;
